import { BaseElement, defineElement } from '@webtides/element-js';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock/lib/bodyScrollLock.es6';
import * as focusTrap from 'focus-trap';

export default class Header extends BaseElement {
    properties() {
        return {
            mobileNavOpen: false,
            firstLevelLink: undefined,
            secondLevelLink: undefined,
        };
    }

    connected() {
        this.focusTrap = focusTrap.createFocusTrap(this.$refs.header, {
            fallbackFocus: this,
        });
    }

    watch() {
        return {
            mobileNavOpen: (mobileNavOpen) => {
                this.$refs.mobileNav.dataset.tailwind = mobileNavOpen ? 'open' : '';

                this.$refs.mobileNavToggles.forEach((mobileNavToggle) => {
                    mobileNavToggle.ariaExpanded = mobileNavOpen ? 'true' : 'false';
                });

                if (mobileNavOpen) {
                    disableBodyScroll(this.$refs.mobileNavContent, {
                        reserveScrollBarGap: true,
                    });
                    this.focusTrap.activate();
                } else {
                    enableBodyScroll(this.$refs.mobileNavContent);
                    this.focusTrap.deactivate();
                }
            },
            firstLevelLink: (linkLabel) => {
                this.secondLevelLink = undefined;
                this.$refs.mobileNavBack.dataset.tailwind = linkLabel ? 'visible' : '';
                this.$refs.desktopNav.dataset.tailwind = linkLabel ? 'open' : '';
                this.$refs.overlay.dataset.tailwind = linkLabel ? 'open' : '';

                if (!this.mobileNavOpen) {
                    if (linkLabel) {
                        disableBodyScroll(this.$refs.header, {
                            reserveScrollBarGap: true,
                        });
                        this.focusTrap.activate();
                    } else {
                        enableBodyScroll(this.$refs.header);
                        this.focusTrap.deactivate();
                    }
                }

                this.$refs.firstLevelLinks.forEach((link) => {
                    link.dataset.tailwind = link.dataset.navLabel === linkLabel ? 'active' : '';
                    link.ariaExpanded = link.dataset.navLabel === linkLabel ? 'true' : 'false';
                });

                this.$refs.secondLevelNavs.forEach((nav) => {
                    nav.dataset.tailwind = nav.dataset.navLabel === linkLabel ? 'active' : '';
                });

                this.$refs.firstLevelQuickLinks.forEach((quickLinks) => {
                    quickLinks.dataset.tailwind = quickLinks.dataset.navLabel === linkLabel ? 'active' : '';
                });

                this.$refs.secondLevelQuickLinks.forEach((quickLinks) => {
                    quickLinks.dataset.tailwind = '';
                });
            },
            secondLevelLink: (linkLabel) => {
                this.$refs.secondLevelLinks.forEach((link) => {
                    link.dataset.tailwind = link.dataset.navLabel === linkLabel ? 'active' : '';
                    link.ariaExpanded = link.dataset.navLabel === linkLabel ? 'true' : 'false';
                });

                this.$refs.thirdLevelNavs.forEach((nav) => {
                    nav.dataset.tailwind = nav.dataset.navLabel === linkLabel ? 'active' : '';
                });

                this.$refs.firstLevelQuickLinks.forEach((quickLinks) => {
                    quickLinks.dataset.tailwind =
                        !linkLabel && quickLinks.dataset.navLabel === this.firstLevelLink ? 'active' : '';
                });

                this.$refs.secondLevelQuickLinks.forEach((quickLinks) => {
                    quickLinks.dataset.tailwind = quickLinks.dataset.navLabel === linkLabel ? 'active' : '';
                });
            },
        };
    }

    events() {
        return {
            this: {
                'mb-accordion-toggle': (event) => {
                    const navLabel = event.item.querySelector('[slot="header"]')?.dataset.navLabel;
                    if (!navLabel) return;
                    this.secondLevelLink = event.open ? navLabel : undefined;
                },
            },
            document: {
                keydown: (event) => {
                    if (event.key === 'Escape') {
                        this.firstLevelLink = undefined;
                    }
                },
            },
            '[ref="overlay"]': {
                click: (event) => {
                    event.preventDefault();
                    this.firstLevelLink = undefined;
                },
            },
            '[ref="desktopNavClose"]': {
                click: (event) => {
                    event.preventDefault();
                    this.firstLevelLink = undefined;
                },
            },
            '[ref="mobileNavToggles[]"]': {
                click: (event) => {
                    event.preventDefault();
                    this.mobileNavOpen = !this.mobileNavOpen;
                    this.firstLevelLink = undefined;
                },
            },
            '[ref="mobileNavBack"]': {
                click: (event) => {
                    event.preventDefault();
                    this.firstLevelLink = undefined;
                },
            },
            '[ref="firstLevelLinks[]"]': {
                click: (event) => {
                    const anchor = event.target.closest('a');
                    if (anchor.hasAttribute('aria-haspopup') && anchor.getAttribute('aria-haspopup') === 'true') {
                        event.preventDefault();
                        const firstLevelLinkLabel = event.target.dataset.navLabel;
                        this.firstLevelLink =
                            this.firstLevelLink === firstLevelLinkLabel ? undefined : event.target.dataset.navLabel;
                    }
                },
            },
            '[ref="secondLevelLinks[]"]': {
                click: (event) => {
                    const anchor = event.target.closest('a');
                    // On mobile the second level link is an accordion header; its toggle event drives the state
                    if (anchor.closest('mb-accordion-item')) return;
                    if (anchor.hasAttribute('aria-haspopup') && anchor.getAttribute('aria-haspopup') === 'true') {
                        event.preventDefault();
                        const secondLevelLinkLabel = event.target.dataset.navLabel;
                        this.secondLevelLink =
                            this.secondLevelLink === secondLevelLinkLabel ? undefined : secondLevelLinkLabel;
                    }
                },
            },
        };
    }
}

defineElement('mb-header', Header);
